import { api, get } from "../api.ts";
import { rerender } from "../rerender.ts";
import { state, wsBase } from "../state.ts";
import type { Group, Member, Members } from "../types.ts";
import { append, busy, confirmDialog, h, toast } from "../ui.ts";

let open: string | null = null;

function editor(g: Group, groups: Group[], people: Member[]) {
  const rows = h("tbody");
  for (const p of people) {
    const m = g.members.find((x) => x.user_id === p.user_id);
    const inBox = h("input", { type: "checkbox", "aria-label": `${p.email} is in ${g.name}`, "data-user": p.user_id, class: "in" }) as HTMLInputElement;
    const lead = h("input", { type: "checkbox", "aria-label": `${p.email} leads ${g.name}`, "data-user": p.user_id, class: "lead" }) as HTMLInputElement;
    inBox.checked = !!m;
    lead.checked = !!m?.lead;
    lead.addEventListener("change", () => {
      if (lead.checked) inBox.checked = true;
    });
    rows.append(h("tr", null, h("td", null, p.name ?? p.email, h("div", { class: "muted small" }, p.email)), h("td", null, inBox), h("td", null, lead)));
  }
  const name = h("input", { type: "text", value: g.name, maxlength: 80, "aria-label": "Group name" }) as HTMLInputElement;
  const parent = h("select", { "aria-label": "Inside" }, h("option", { value: "" }, "Top level")) as HTMLSelectElement;
  for (const x of groups) if (x.id !== g.id && !x.path.startsWith(`${g.path} / `)) parent.append(h("option", { value: x.id }, x.path));
  parent.value = g.parent_id ?? "";

  const save = h("button", { type: "button", class: "primary" }, "Save group") as HTMLButtonElement;
  save.addEventListener("click", () =>
    busy(save, async () => {
      const members = Array.from(rows.querySelectorAll<HTMLInputElement>("input.in"))
        .map((x) => {
          const lead = rows.querySelector<HTMLInputElement>(`input.lead[data-user="${x.dataset.user}"]`)!;
          return x.checked || lead.checked ? { user_id: x.dataset.user!, lead: lead.checked } : null;
        })
        .filter((x) => x !== null);
      if (name.value.trim() !== g.name || (parent.value || null) !== g.parent_id) {
        await api("PATCH", `${wsBase()}/groups/${g.id}`, { name: name.value.trim(), parent_id: parent.value || null });
      }
      const r = await api<{ tracks_given: number }>("PUT", `${wsBase()}/groups/${g.id}/members`, { members });
      toast(`Group saved${r.tracks_given ? `. New members got ${r.tracks_given === 1 ? "1 track" : `${r.tracks_given} tracks`}` : ""}.`);
      state.overview = null;
      rerender();
    }),
  );
  const del = h("button", { type: "button", class: "danger" }, "Delete group") as HTMLButtonElement;
  del.addEventListener("click", () =>
    busy(del, async () => {
      if (!(await confirmDialog(`Delete ${g.path}?`, "The groups inside it go too. People stay in the company and keep their tracks.", "Delete group", true))) return;
      await api("DELETE", `${wsBase()}/groups/${g.id}`);
      open = null;
      toast("Group deleted.");
      state.overview = null;
      rerender();
    }),
  );
  return h(
    "section",
    { class: "editor" },
    h("h2", null, g.path),
    h("div", { class: "form-row" }, h("label", { class: "field" }, "Name", name), h("label", { class: "field" }, "Inside", parent)),
    h("p", { class: "muted small" }, "A lead sees this group and every group inside it, and can give them tracks and reminders."),
    people.length
      ? h("div", { class: "table-wrap" }, h("table", null, h("thead", null, h("tr", null, h("th", null, "Person"), h("th", null, "In group"), h("th", null, "Leads"))), rows))
      : h("p", { class: "empty" }, "Invite people first."),
    h("div", { class: "actions" }, save, del),
  );
}

export async function renderGroups(root: HTMLElement) {
  const [{ groups }, members] = await Promise.all([
    get<{ groups: Group[] }>(`${wsBase()}/groups`),
    get<Members>(`/v1/workspaces/${encodeURIComponent(state.ws!.id)}/members`),
  ]);
  const tree = h("ul", { class: "tree" });
  const draw = (parentId: string | null, into: HTMLElement) => {
    for (const g of groups.filter((x) => (x.parent_id ?? null) === parentId)) {
      const leads = g.members.filter((m) => m.lead).length;
      const li = h(
        "li",
        null,
        h(
          "button",
          { type: "button", class: open === g.id ? "node on" : "node", "aria-expanded": open === g.id ? "true" : "false", onclick: () => ((open = open === g.id ? null : g.id), rerender()) },
          g.name,
          h("span", { class: "muted" }, ` ${g.members.length} ${g.members.length === 1 ? "person" : "people"}${leads ? `, ${leads} ${leads === 1 ? "lead" : "leads"}` : ", no lead"}`),
        ),
      );
      const sub = h("ul", { class: "tree" });
      draw(g.id, sub);
      if (sub.childElementCount) li.append(sub);
      into.append(li);
    }
  };
  draw(null, tree);

  const name = h("input", { type: "text", required: true, maxlength: 80, placeholder: "Data team", "aria-label": "New group name" }) as HTMLInputElement;
  const parent = h("select", { "aria-label": "Inside" }, h("option", { value: "" }, "Top level")) as HTMLSelectElement;
  for (const g of groups) parent.append(h("option", { value: g.id }, g.path));
  const add = h("button", { type: "submit", class: "primary" }, "Add group") as HTMLButtonElement;
  const form = h("form", { class: "invite" }, name, parent, add);
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    void busy(add, async () => {
      const r = await api<{ group: { id: string } }>("POST", `${wsBase()}/groups`, { name: name.value.trim(), parent_id: parent.value || null });
      open = r.group.id;
      rerender();
    });
  });

  append(
    root,
    h("header", { class: "page-head" }, h("div", null, h("h1", null, "Groups and leads"), h("p", { class: "muted" }, "Departments, teams and batches, in any depth."))),
    h("section", null, h("h2", null, "Add a group"), form),
    groups.length ? h("section", null, h("h2", null, "All groups"), tree) : h("p", { class: "empty" }, "No groups yet. Add one, then put people in it and pick its lead."),
  );
  const g = groups.find((x) => x.id === open);
  if (g) root.append(editor(g, groups, members.members));
}
